// GLBFetcher
// One fetch per hash. The fragment GLB carries three meshes inside —
// gltf.meshes[0] = fine, [1] = medium, [2] = coarse. We extract them as
// { geometry, material, name } slots and hand them to the cache.
//
// URL convention (post-schema-v3): the per-tile sidecar JSON carries a
// `fragmentUrls` lookup mapping rawHash → relative URL. The relative
// URL is the writer's filename (sanitized + discriminator), already
// URL-safe — no client-side encoding required.
//
// We deliberately do NOT compute URLs from the hash. The C#
// FragmentGlbWriter applies a non-trivial sanitization
// (allowlist [A-Za-z0-9_.-], collapse runs, append SHA-256-short
// discriminator) and the viewer must NOT mirror that algorithm —
// that's exactly the kind of drift that produces silent 404s when one
// side changes. The writer is the single source of truth; the viewer
// reads what the writer told it.
//
// === BASE-URL SPLIT (project-scoped vs global fragments) ===
//
// R2 layout has two roots:
//
//   uploads/{projectId}/manifest.json.gz
//   uploads/{projectId}/tiles/tile_*.json.gz
//   uploads/{projectId}/library.*.glb.gz
//   uploads/fragments/{sanitized-name}.glb.gz   ← global, dedup'd
//
// Fragment GLBs live in a SHARED top-level directory because the same
// (geometry, material) combination — keyed by hash — is reused across
// projects. Putting them under the project prefix would force re-upload
// per project AND make the cache useless. The C# uploader writes them
// to `uploads/fragments/...`, never `uploads/{projectId}/fragments/...`.
//
// The viewer derives `baseUrl` from `manifestUrl` (project-scoped), so
// joining baseUrl + a `fragments/...` relUrl naively produces a
// project-scoped path that 404s on R2. We accept a separate
// `fragmentBaseUrl` (one level up from baseUrl, i.e. just `.../uploads`)
// and route any relUrl whose first path segment is `fragments` through
// that base instead.
//
// Body delivery: R2 stores the file gzipped (.gz extension) AND serves
// it with Content-Encoding: gzip. The browser decompresses transparently
// before res.arrayBuffer() resolves, so GLTFLoader sees raw GLB bytes.

import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { deriveFragmentBase } from './fragmentBase';
import { fetchModel, applyTilesBase } from './streamBase';
import SlotQueue, { demandOf } from './slotQueue';
import { decodePack, packDecoderReady, partsFromArrays } from './packFormat';
import PackDecodePool from './PackDecodePool';
import fetchLedger, { shareInFlight } from './fetchLedger';

const LOD_COUNT = 3;

// Cap on concurrent network fetches. Back to 96 — the 48 cap was added
// thinking it would relieve main-thread pressure during 4000-element
// shell selections, but the actual cause of that freeze was the per-item
// applicator loop in the WS 'click' handler (since fixed via the bulk-
// select path through viewer.bulkSelectSocket + store.bulkHighlight).
// With that gone, 96 fetch concurrency is back to being the right value
// for cold-load throughput.
const DEFAULT_MAX_CONCURRENT_FETCHES = 96;

// Transient errors get retried with exponential backoff. Persistent
// errors (4xx, malformed GLB) fail fast.
const MAX_RETRIES = 3;
const RETRY_BACKOFF_MS = [250, 500, 1000];

// A URL this session already wrote to IndexedDB is read back with a long
// wait instead of the cache's default 250 ms. Round 10 (headless staging
// SpaceX cold load): 3,106 of 6,924 pack reads timed out while the main
// thread was busy, and every timeout became a network request for bytes
// the cache was holding. For a URL we know we stored, a hit is almost
// certain, so waiting for it is cheaper than the download it replaces.
const STORED_READ_TIMEOUT_MS = 3000;

function _isTransientFetchError(err) {
  if (!err) return false;
  // Network-layer failures surface as TypeError in fetch(). The browser
  // also stamps the underlying cause into the message for HTTP/2 protocol
  // errors and connection resets.
  if (err.name === 'TypeError') return true;
  const msg = String(err.message || '');
  if (msg.includes('HTTP2_PROTOCOL_ERROR')) return true;
  if (msg.includes('ERR_NETWORK')) return true;
  if (msg.includes('Failed to fetch')) return true;
  // 5xx from our own throw site in _doFetchOnce.
  if (/^GLBFetcher 5\d\d/.test(msg)) return true;
  return false;
}

export default class GLBFetcher {
  constructor({
    baseUrl, fragmentBaseUrl, cache, blobCache = null,
    maxConcurrentFetches = DEFAULT_MAX_CONCURRENT_FETCHES,
    // OPTIONAL: GLTFWorkerPool instance. When provided, parse work is
    // offloaded to Web Workers instead of running on the main thread.
    // Falls back to main-thread GLTFLoader.parse if the worker is
    // unavailable or rejects (e.g., transient worker failure).
    workerPool = null,
    // OPTIONAL: content-derived cache-bust token (typically the
    // export-timestamp parsed from manifest.projectName). When set,
    // appended as `?v=<token>` to every fragment URL so the IDB +
    // HTTP caches automatically miss on a fresh export. Empty/null
    // means "no bust" — fragments cache by raw URL like before.
    exportVersion = null,
  }) {
    if (!baseUrl) throw new Error('GLBFetcher: baseUrl required');
    if (!cache)   throw new Error('GLBFetcher: cache required');
    // blobCache is optional. When provided, fragment GLBs go through
    // an IndexedDB cache-aside so reload-of-same-project hits local
    // storage instead of R2. See IDBBlobCache for behavior + budget.
    this.blobCache = blobCache;
    this.baseUrl = baseUrl.replace(/\/+$/, '');

    // fragmentBaseUrl is optional — when omitted it is derived from
    // baseUrl by walking up to the `/uploads` prefix that
    // `uploads/fragments/...` is served from.
    //
    // This used to strip exactly ONE trailing segment, on the assumption
    // that baseUrl is always `.../uploads/{projectId}`. Versioned exports
    // break that assumption: a manifest at
    // `.../uploads/{projectId}/versions/{exportVersion}/manifest.json.gz`
    // yields a baseUrl three segments deep, and stripping one lands on
    // `.../versions` — so every fragment GLB 404s.
    //
    // That failure is silent in the worst way. The manifest loads, the
    // sidecars load, the model tree populates, the counts look right, and
    // the scene is empty. It reads as "the viewer is broken" rather than
    // "the base url is wrong by two segments".
    //
    // Anchoring on `/uploads` instead of counting segments is correct for
    // both layouts and for any future nesting.
    const explicitFragBase = fragmentBaseUrl
      ? fragmentBaseUrl.replace(/\/+$/, '')
      : null;
    this.fragmentBaseUrl = explicitFragBase ?? deriveFragmentBase(this.baseUrl);

    this.cache = cache;
    this.loader = new GLTFLoader();

    // Cache-bust query string for fragment URLs. Empty when no
    // exportVersion was passed — buildUrl() emits raw URLs in that
    // case, preserving prior behavior.
    this.exportVersionQuery = exportVersion
      ? '?v=' + encodeURIComponent(exportVersion)
      : '';
    this.workerPool = workerPool;
    // Counters surfaced via stats() so we can tell whether worker offload
    // is actually doing anything (vs. silently falling back to main thread).
    this._totalWorkerParses = 0;
    this._totalMainThreadParses = 0;
    this._totalWorkerFallbacks = 0;
    this.inFlight = new Map(); // hash -> Promise<libraryParts>

    // Main-thread parse is reserved for worker-error recovery only.
    // The 32-slot main-thread semaphore from the prior split-parse model
    // was removed because it put parse work back on main thread when
    // workers were busy — which was exactly what was starving
    // instantiation. See _parseGLB for the new worker-only path.

    // Global fetch semaphore. The slot count is bumped on acquire and
    // either decremented or directly handed off to a waiter on release.
    // Acquire never re-increments after a queued wait — the slot was
    // transferred to us by _releaseSlot's direct hand-off path.
    this.maxConcurrentFetches = maxConcurrentFetches;
    this._activeFetches = 0;
    // Resolvers waiting for a slot, lowest priority value first (see slotQueue).
    this._fetchQueue = new SlotQueue();

    // Counters for diagnostics. Surfaced via stats().
    this._totalFetches = 0;
    this._totalRetries = 0;
    this._totalFailures = 0;
  }

  /**
   * Raise or lower the number of fetches allowed in flight at once.
   * Raising it releases waiters immediately so the new slots are used
   * right away; lowering it only stops NEW acquires, fetches already
   * running are never cancelled. Used by the controller's cold-start
   * burst (see streamingController `coldStartMs`).
   */
  setMaxConcurrentFetches(n) {
    const next = Math.max(1, Math.floor(n) || 1);
    if (next === this.maxConcurrentFetches) return this.maxConcurrentFetches;
    this.maxConcurrentFetches = next;
    while (this._fetchQueue.length > 0 && this._activeFetches < this.maxConcurrentFetches) {
      const waiter = this._fetchQueue.shift();   // skips waiters nobody wants any more
      if (!waiter) break;
      this._activeFetches++;
      waiter();
    }
    return this.maxConcurrentFetches;
  }

  // `priority`: a number, or a demand from wantOf() ({ rank, isStale }) -
  // ranked live and dropped when nobody wants it (see slotQueue).
  _acquireSlot(priority = 0) {
    if (this._closed) { const err = new Error('cancelled: viewer closed'); err.cancelled = true; return Promise.reject(err); }
    if (this._activeFetches < this.maxConcurrentFetches) {
      this._activeFetches++;
      return Promise.resolve();
    }
    const demand = priority && typeof priority === 'object' ? priority : null;
    return new Promise((resolve, reject) => this._fetchQueue.push(
      resolve,
      demand ? demand.rank : priority,
      { reject, isStale: demand ? demand.isStale : null }
    ));
  }

  // Re-rank everything still waiting for a slot (the camera moved).
  reprioritize() {
    return this._fetchQueue && typeof this._fetchQueue.reprioritize === 'function'
      ? this._fetchQueue.reprioritize() : false;
  }

  // Several requesters sharing one download (a fragment, a pack): it ranks
  // by the most wanted of them and is stale only when every one of them is.
  // A plain number priority is a requester that never goes stale.
  static _demand(waiters) {
    return demandOf(waiters);
  }

  // The viewer is gone: cancel everything waiting for a slot and refuse
  // new work. Downloads already running finish and are ignored.
  close() {
    this._closed = true;
    if (this._fetchQueue) this._fetchQueue.clear();
  }

  _releaseSlot() {
    // Direct hand-off: the slot we held is transferred to the next
    // waiter without ever dropping the active count. This avoids a
    // window where _activeFetches dips below the cap and a freshly-
    // arriving caller jumps the queue ahead of the waiter. The queue
    // skips waiters nobody wants any more.
    const next = this._fetchQueue.length > 0 ? this._fetchQueue.shift() : undefined;
    if (next) {
      next();
    } else {
      this._activeFetches--;
    }
  }

  /**
   * Build the absolute URL from a relative URL provided by the manifest
   * or sidecar. The relative URL already includes its top-level
   * subdirectory prefix (`fragments/`, `tiles/`, etc.) and the
   * sanitized filename. We append `.gz` so R2's Content-Encoding: gzip
   * header kicks in.
   *
   * Fragment relUrls (those starting with `fragments/`) are joined to
   * `fragmentBaseUrl` so they hit the SHARED `/uploads/fragments/...`
   * R2 prefix instead of `/uploads/{projectId}/fragments/...`, which
   * doesn't exist and 404s. Everything else (tiles, library GLBs) stays
   * project-scoped via `baseUrl`.
   */
  /**
   * ONE FILE PER LEVEL OF DETAIL (exporter: FragmentGlbWriter.SplitLodFiles).
   *
   * A combined fragment carries fine + medium + coarse, so a tile 300 m
   * away downloads the fine geometry it will never draw - on the live
   * SpaceX export, 4.33 GB of fragments, most of it detail nobody sees.
   * Split, the fine file keeps its name and the other two sit beside it
   * as "<stem>.l1.glb" / "<stem>.l2.glb", so the viewer fetches the level
   * it is about to draw, and a finer one when the camera comes closer.
   *
   * Turned on by the manifest's `fragmentLodFiles`. Off, or when a level
   * file is missing, everything falls back to the combined file - which
   * is what makes an old export and a new one both work.
   */
  setLodFiles(on, suffixes) {
    this.lodFiles = !!on;
    this.lodSuffixes = Array.isArray(suffixes) && suffixes.length === 3 ? suffixes : ['', '.l1', '.l2'];
  }

  /**
   * Fetch tiles/fragments from models.bidlight.com instead of /stream.
   * Turned on by the manifest's `modelsBucket` - see streamBase.fetchModel.
   */
  setModelsHost(on) {
    this.modelsHost = !!on;
  }

  /**
   * Fragment packs (back/utils/fragmentPack.js): many fragments per
   * request, typed arrays straight onto the bytes, no GLB parse. Turned on
   * by the manifest's `fragmentPacks`; loads the pack index (hash -> pack
   * number) before streaming starts. A fragment missing from the index, or
   * a pack that fails for any reason, loads from its own GLB as before.
   */
  async setPacks(spec) {
    this.packHomes = null;
    if (!spec || !spec.index) return false;
    try {
      // Version 2 packs are meshopt-coded; the decoder (WASM) must be ready.
      if (Number(spec.v) >= 2) await packDecoderReady();
      const buf = await this._fetchWithRetry(this.buildUrl(String(spec.index).replace(/\.gz$/i, '')), -1);
      const index = JSON.parse(new TextDecoder().decode(new Uint8Array(buf)));
      this.packHomes = new Map(Object.entries(index.homes || {}));
      // Packs sit beside their index: tiles/packs/ (v1), tiles/packs2/ (v2).
      this.packDir = String(spec.index).replace(/\/[^/]*$/, '') || 'tiles/packs';
      this._packInFlight = new Map();
      this._packStats = { packs: 0, fromPack: 0, prefilled: 0, fallbacks: 0 };
      return true;
    } catch (err) {
      console.warn('[GLBFetcher] pack index unavailable, loading fragments one by one:', err && err.message);
      this.packHomes = null;
      return false;
    }
  }

  // One pack at one level, fetched and decoded once however many
  // fragments ask for it at the same time.
  _loadPack(packNo, lod, priority) {
    const key = `${packNo}|${lod}`;
    const pending = this._packInFlight.get(key);
    if (pending) { pending.waiters.push(priority); return pending; }
    const waiters = [priority];
    const demand = GLBFetcher._demand(waiters);
    const promise = (async () => {
      const url = this.buildUrl(`${this.packDir || 'tiles/packs'}/${packNo}_l${lod}.bin`);
      let buf = await this._readCached(url);
      if (!buf) {
        const skipped = this.blobCache && this.blobCache._skipNextReads;
        buf = await this._fetchWithRetry(url, demand);
        this._storeCached(url, buf);
        // Same rule as _doFetchUrl: after a cache flip (?cache=clear, a new
        // export) the first network fetch lets reads through again. The
        // pack path never cleared it, so after a flip every pack re-read
        // in the session skipped IndexedDB and went to the network.
        if (skipped) this.blobCache._skipNextReads = false;
      }
      this._packStats.packs++;
      this._packStats.bytes = (this._packStats.bytes || 0) + buf.byteLength;
      // Decoded off the main thread when a worker is available; the
      // worker gets a copy because the cache write above may still be
      // reading this buffer. decodeMs counts main-thread time only.
      if (!this._packDecoder) this._packDecoder = new PackDecodePool();
      if (this._packDecoder.available) {
        try {
          const decoded = await this._packDecoder.decode(buf.slice(0));
          const t1 = performance.now();
          const parts = partsFromArrays(decoded);
          this._packStats.workerDecodes = (this._packStats.workerDecodes || 0) + 1;
          this._packStats.decodeMs = Math.round(((this._packStats.decodeMs || 0) + performance.now() - t1) * 10) / 10;
          return parts;
        } catch (e) {
          this._packStats.workerFallbacks = (this._packStats.workerFallbacks || 0) + 1;
        }
      }
      const t0 = performance.now();
      const parts = decodePack(buf).parts;
      this._packStats.decodeMs = Math.round(((this._packStats.decodeMs || 0) + performance.now() - t0) * 10) / 10;
      return parts;
    })();
    promise.waiters = waiters;
    this._packInFlight.set(key, promise);
    promise.then(() => this._packInFlight.delete(key), () => this._packInFlight.delete(key));
    return promise;
  }

  // A fragment's level from its pack. Every other fragment in the pack
  // gets that level too, into slots that are still empty - an existing
  // slot may be drawing and is never replaced.
  async _fetchViaPack(hash, lod, priority) {
    const parts = await this._loadPack(this.packHomes.get(hash), lod, priority);
    const mine = parts.get(hash);
    if (!mine) throw new Error(`pack ${this.packHomes.get(hash)} has no ${hash}`);
    parts.forEach((part, h) => {
      if (h === hash) return;
      const lib = this.cache.getLibrary(h);
      if (lib && lib[lod]) return;
      this.cache.putLibrary(h, this._levelInto(lib, part, lod), 'prefill');
      this._packStats.prefilled++;
    });
    this._packStats.fromPack++;
    return [mine];
  }

  _levelInto(existing, part, lod) {
    const merged = (existing || [null, null, null]).slice(0, 3);
    while (merged.length < 3) merged.push(null);
    if (!merged[lod]) merged[lod] = part;
    return merged;
  }

  lodUrl(relUrl, lod) {
    if (!this.lodFiles || !lod) return relUrl;
    const suffix = (this.lodSuffixes || ['', '.l1', '.l2'])[lod] || '';
    if (!suffix) return relUrl;
    return String(relUrl).replace(/\.glb$/i, `${suffix}.glb`);
  }

  // Direct-to-models exports: tile-relative paths live under the version
  // prefix (streamBase.applyTilesBase).
  setTilesBase(tilesBase) { this.tilesBase = tilesBase || null; }

  buildUrl(relUrl) {
    if (!relUrl) throw new Error('GLBFetcher: relUrl required');
    // Tolerate leading slashes — joining `${base}/${relUrl}` should
    // produce exactly one separator no matter what the caller hands us.
    const trimmed = relUrl.replace(/^\/+/, '');
    const isFragment = trimmed.toLowerCase().startsWith('fragments/');
    const base = isFragment ? this.fragmentBaseUrl : this.baseUrl;
    const rel = isFragment ? trimmed : applyTilesBase(trimmed, this.tilesBase);
    return `${base}/${rel}.gz${this.exportVersionQuery}`;
  }

  /**
   * Fetch a fragment by raw hash AND its tile-provided relative URL.
   * Cache key is the raw hash (canonical fragment identity, shared
   * across tiles) — the URL is just how to retrieve it.
   *
   * @param {string} hash  - Raw fragment hash from the sidecar's instance.h
   * @param {string} relUrl - Relative URL from sidecar.fragmentUrls[hash]
   */
  // `priority`: lower is fetched sooner when slots are contended - the
  // controller passes the tile's distance, tiles in view first.
  async fetchFragment(hash, relUrl, wantLod = 0, priority = 0) {
    const cached = this.cache.getLibrary(hash);
    // Split or packed: the cached array fills in a level at a time, so a
    // hit only counts when it holds the level being asked for.
    const packed = !!(this.packHomes && this.packHomes.has(hash));
    const byLevel = this.lodFiles || packed;
    if (cached && (!byLevel || cached[wantLod])) return cached;

    const lod = byLevel ? Math.max(0, Math.min(2, Number(wantLod) || 0)) : 0;
    const key = byLevel ? `${hash}|${lod}` : hash;
    const pending = this.inFlight.get(key);
    if (pending) { if (pending.waiters) pending.waiters.push(priority); return pending; }

    const waiters = [priority];
    const demand = GLBFetcher._demand(waiters);
    const load = packed
      ? this._fetchViaPack(hash, lod, demand).catch((err) => {
        // Nobody wants it any more: do not go and fetch the fragment itself.
        if (err && err.cancelled) throw err;
        this._packStats.fallbacks++;
        if (this._packStats.fallbacks <= 5) console.warn('[GLBFetcher] pack miss, loading the fragment itself:', err && err.message);
        return this._doFetch(hash, relUrl, lod, demand);
      })
      : this._doFetch(hash, relUrl, lod, demand);
    const promise = load
      .then(parts => {
        this.cache.putLibrary(hash, packed && parts.length === 1
          ? this._levelInto(this.cache.getLibrary(hash), parts[0], lod)
          : this._mergeParts(hash, parts, lod));
        this.inFlight.delete(key);
        return this.cache.getLibrary(hash) || parts;
      })
      .catch(err => {
        this.inFlight.delete(key);
        throw err;
      });

    promise.waiters = waiters;
    this.inFlight.set(key, promise);
    return promise;
  }

  // A split file parses to ONE slot, which lands at its own level beside
  // whatever levels were fetched before. A combined file already is the
  // whole array.
  _mergeParts(hash, parts, lod) {
    if (!this.lodFiles) return parts;
    const incoming = Array.isArray(parts) ? parts.filter(Boolean) : [];
    if (incoming.length > 1) return parts;        // fell back to a combined file
    const merged = (this.cache.getLibrary(hash) || [null, null, null]).slice(0, 3);
    while (merged.length < 3) merged.push(null);
    merged[lod] = incoming[0] || merged[lod] || null;
    return merged;
  }

  async _doFetch(hash, relUrl, lod = 0, priority = 0) {
    // A level file that is not there - an older export, or an upload that
    // did not carry the levels - falls back to the fragment's own file,
    // which holds every level.
    if (this.lodFiles && lod > 0) {
      try {
        return await this._doFetchUrl(hash, this.lodUrl(relUrl, lod), priority);
      } catch (err) {
        if (!/\b404\b|not found/i.test(String(err && err.message))) throw err;
        this._lodFileMisses = (this._lodFileMisses || 0) + 1;
      }
    }
    return this._doFetchUrl(hash, relUrl, priority);
  }

  async _doFetchUrl(hash, relUrl, priority = 0) {
    const url = this.buildUrl(relUrl);

    // IDB cache-aside. The cache stores raw GLB bytes — the same bytes
    // R2 would return — keyed by absolute URL. Cache key is URL not
    // hash because (a) URL is unambiguous across projects, and (b)
    // hash-to-URL mapping could in theory be redirected in the future
    // and we don't want stale-content silently served as the new hash.
    //
    // Worst case (cache miss): one extra IDB `get` call, ~1-2 ms.
    // Best case (cache hit): zero network, GLTFLoader.parse runs on
    // bytes we already had. Big perceived-perf win on reload.
    // `_skipNextReads` is flipped by ManifestLoader when it detects a
    // new exportVersion + has just clearAll()'d the cache. Honoring it
    // here forces the first fetch-per-URL to hit the network even if
    // any stale entry survived the clear (e.g., a write that finished
    // mid-clear). The flag self-clears after the first miss-driven
    // network fetch — see below.
    const skipIdbRead = this.blobCache && this.blobCache._skipNextReads;
    let buf = await this._readCached(url);

    if (!buf) {
      buf = await this._fetchWithRetry(url, priority);
      // Fire-and-forget the cache write so the materialize path doesn't
      // wait for IDB. Errors swallowed — see IDBBlobCache.put.
      this._storeCached(url, buf);
      // After the first miss + network fetch following a cache flip,
      // un-suspend IDB reads — the freshly-written entry is correct
      // and future reads should go through the fast path again.
      if (skipIdbRead) this.blobCache._skipNextReads = false;
    }

    // === PARSE PATH — WORKERS ONLY ===
    //
    // Workers handle ALL parse work. Main thread does instantiation only
    // (acquire slots in InstancedMesh, write transforms, set userData).
    // The previous version had a "main-thread parse fallback when worker
    // queue is busy" path which put parse compete with instantiation —
    // negating the worker offload. With 64 workers and parse-only-in-worker,
    // main thread stays free for acquire() / setMatrixAt() / render.
    //
    // Main-thread parse is now ONLY a last-resort fallback for worker
    // errors (worker rejection / empty result), not a routine path.
    if (this.workerPool && this.workerPool.available) {
      try {
        this._totalWorkerParses++;
        const { libraryParts } = await this.workerPool.parseGLB(buf);
        if (libraryParts && libraryParts.some(Boolean)) {
          return libraryParts;
        }
        this._totalWorkerFallbacks++;
      } catch (err) {
        this._totalWorkerFallbacks++;
        console.warn(`[GLBFetcher] worker parse failed, falling back to main thread for ${url.slice(-80)}: ${err.message}`);
      }
    }

    // Error-recovery main-thread parse. No semaphore — this should be
    // very rare (a worker has to actually reject). If you see this path
    // fire frequently, the worker pool has a bug, not a capacity issue.
    this._totalMainThreadParses++;
    return await this._parseOnMainThread(buf, url);
  }

  // IndexedDB read for model bytes (packs and fragment GLBs). Null on a
  // miss, a timeout, a corrupt store, or while reads are suspended after a
  // cache flip. A URL this fetcher stored earlier in the session waits
  // longer for its answer (STORED_READ_TIMEOUT_MS).
  async _readCached(url) {
    const bc = this.blobCache;
    if (!bc || bc._skipNextReads) return null;
    const stored = !!(this._storedUrls && this._storedUrls.has(url));
    try {
      const buf = await bc.get(url, stored ? { timeoutMs: STORED_READ_TIMEOUT_MS } : undefined);
      if (stored) {
        if (buf) this._storedRereadHits = (this._storedRereadHits || 0) + 1;
        else this._storedRereadMisses = (this._storedRereadMisses || 0) + 1;
      }
      return buf || null;
    } catch (e) {
      return null;   // a corrupt IDB shouldn't break streaming
    }
  }

  // Fire-and-forget cache write; remembered so a re-read waits for it.
  _storeCached(url, buf) {
    if (!this.blobCache || !buf) return;
    if (!this._storedUrls) this._storedUrls = new Set();
    this._storedUrls.add(url);
    // eslint-disable-next-line no-void
    void this.blobCache.put(url, buf);
  }

  _parseOnMainThread(buf, url) {
    return new Promise((resolve, reject) => {
      this.loader.parse(buf, '', gltf => {
        const collected = [];
        gltf.scene.traverse(node => {
          if (node.isMesh && collected.length < LOD_COUNT) {
            collected.push({
              geometry: node.geometry,
              material: node.material,
              name: node.name,
            });
          }
        });

        if (collected.length === 0) {
          reject(new Error(`GLBFetcher: no meshes in ${url}`));
          return;
        }

        const libraryParts = new Array(LOD_COUNT).fill(null);
        for (let i = 0; i < collected.length && i < LOD_COUNT; i++) {
          libraryParts[i] = collected[i];
        }
        for (let i = 0; i < LOD_COUNT; i++) {
          if (!libraryParts[i]) {
            libraryParts[i] = libraryParts.find(Boolean) ?? null;
          }
        }

        resolve(libraryParts);
      }, reject);
    });
  }

  // Network fetch wrapped by the global semaphore + bounded retry.
  // Each attempt acquires a slot, runs the fetch, releases the slot
  // BEFORE the backoff sleep so other queued fetches can proceed during
  // the wait. Only transient errors (network failures, HTTP/2 protocol
  // errors, 5xx) are retried — 4xx and malformed-GLB throws fail fast.
  //
  // One request per URL at a time across every caller - packs, fragment
  // files and the pack index, here and in ManifestLoader (fetchLedger's
  // shareInFlight, keyed by the URL that is also the IndexedDB key). A
  // failed or cancelled request frees the URL, so the next ask retries.
  _fetchWithRetry(url, priority = 0) {
    return shareInFlight(url, () => this._fetchWithRetryOnce(url, priority));
  }

  async _fetchWithRetryOnce(url, priority = 0) {
    let lastErr = null;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      await this._acquireSlot(priority);
      let begun = false;
      let ok = false;
      let got = 0;
      try {
        this._totalFetches++;
        fetchLedger.begin(url, attempt > 0);
        begun = true;
        const res = await fetchModel(url, { mode: 'cors' }, this.modelsHost);
        if (!res.ok) {
          const err = new Error(`GLBFetcher ${res.status} for ${url}`);
          // 4xx is a permanent client error — don't retry, don't waste
          // capacity. 5xx falls through to the retry branch below.
          if (res.status >= 400 && res.status < 500) {
            this._totalFailures++;
            // [DIAG-EXPORT] permanent fragment 4xx — usually means the
            // fragment was never uploaded to R2 (exporter dropped it) or
            // the URL was built wrong. Either way the viewer will show
            // an invisible hole where this instance was supposed to land.
            console.warn('[DIAG-EXPORT] fragment-fail-4xx', JSON.stringify({ status: res.status, url }));
            throw err;
          }
          throw err;
        }
        const body = await res.arrayBuffer();
        ok = true;
        got = body.byteLength;
        return body;
      } catch (err) {
        lastErr = err;
        const transient = _isTransientFetchError(err);
        if (!transient || attempt === MAX_RETRIES) {
          this._totalFailures++;
          console.warn('[DIAG-EXPORT] fragment-fail', JSON.stringify({ url, msg: String(err?.message || err) }));
          throw err;
        }
        this._totalRetries++;
      } finally {
        if (begun) fetchLedger.end(url, ok, got);
        this._releaseSlot();
      }
      // Backoff with small jitter, OUTSIDE the slot — the slot is
      // released so other fetches can use the wait window.
      const base = RETRY_BACKOFF_MS[attempt] ?? RETRY_BACKOFF_MS[RETRY_BACKOFF_MS.length - 1];
      const jitter = Math.floor(Math.random() * 100);
      await new Promise(r => setTimeout(r, base + jitter));
    }
    throw lastErr;
  }

  stats() {
    return {
      librariesInFlight: this.inFlight.size,
      activeFetches: this._activeFetches,
      queuedFetches: this._fetchQueue.length,
      packs: this.packHomes ? { indexed: this.packHomes.size, ...this._packStats } : null,
      maxConcurrentFetches: this.maxConcurrentFetches,
      totalFetches: this._totalFetches,
      totalRetries: this._totalRetries,
      totalFailures: this._totalFailures,
      totalWorkerParses: this._totalWorkerParses,
      totalMainThreadParses: this._totalMainThreadParses,
      totalWorkerFallbacks: this._totalWorkerFallbacks,
      storedRereads: { hits: this._storedRereadHits || 0, misses: this._storedRereadMisses || 0 },
      workerPool: this.workerPool?.stats?.() ?? null,
    };
  }
}